import { startTransition, useCallback, useMemo, useRef, useState } from "react"

import { StepStackContext, StepStackContextValue } from "./StepStack.context"
import { StepStackRootProps } from "./StepStack.types"
import StepStackBackButton from "./StepStackBackButton/StepStackBackButton"
import StepStackContent from "./StepStackContent/StepStackContent"
import StepStackItem from "./StepStackItem/StepStackItem"
import StepStackItemContent from "./StepStackItemContent/StepStackItemContent"
import StepStackItemGroup from "./StepStackItemGroup/StepStackItemGroup"
import StepStackNextButton from "./StepStackNextButton/StepStackNextButton"

function StepStackRoot(props: StepStackRootProps) {
    const {
        children,
        currentItemId: controlledItemId,
        onRequestItem,
        maxClickableItemId,
        errorItemIds = [],
        defaultItemId,
        transitionDurationMS = 200,
    } = props

    const registeredIdsRef = useRef<string[]>([])
    const [orderedIds, setOrderedIds] = useState<string[]>([])
    const [internalCurrentItemId, setInternalCurrentItemId] = useState<string | null>(defaultItemId ?? null)

    const isControlled = controlledItemId !== undefined
    const currentItemId: string | null = isControlled ? (controlledItemId ?? null) : internalCurrentItemId

    const registerItem = useCallback(
        (id: string) => {
            if (!registeredIdsRef.current.includes(id)) {
                registeredIdsRef.current = [...registeredIdsRef.current, id]
                startTransition(() => {
                    setOrderedIds([...registeredIdsRef.current])
                })
            }

            if (!isControlled) {
                setInternalCurrentItemId((prev) => prev ?? registeredIdsRef.current[0] ?? null)
            }

            return () => {
                registeredIdsRef.current = registeredIdsRef.current.filter((i) => i !== id)
                startTransition(() => {
                    setOrderedIds([...registeredIdsRef.current])
                })
            }
        },
        [isControlled],
    )

    const navigateTo = useCallback(
        (id: string) => {
            if (isControlled) {
                onRequestItem?.(id)
            } else {
                setInternalCurrentItemId(id)
            }
        },
        [isControlled, onRequestItem],
    )

    const errorItemIdsSet = useMemo(() => new Set<string>(errorItemIds), [errorItemIds])

    const contextValue = useMemo<StepStackContextValue>(
        () => ({
            orderedIds,
            currentItemId,
            maxClickableItemId: maxClickableItemId ?? null,
            errorItemIds: errorItemIdsSet,
            navigateTo,
            registerItem,
            transitionDurationMS,
        }),
        [
            orderedIds,
            currentItemId,
            maxClickableItemId,
            errorItemIdsSet,
            navigateTo,
            registerItem,
            transitionDurationMS,
        ],
    )

    return <StepStackContext.Provider value={contextValue}>{children}</StepStackContext.Provider>
}

export default {
    Root: StepStackRoot,
    ItemGroup: StepStackItemGroup,
    Item: StepStackItem,
    Content: StepStackContent,
    ItemContent: StepStackItemContent,
    NextButton: StepStackNextButton,
    BackButton: StepStackBackButton,
}
